import React, { useState } from "react";
import { PlugZap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";

const Field = ({ label, children, testId, wide }) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <div className="eyebrow text-[9px]">{label}</div>
    <div className="mt-1 text-sm break-all" style={{ color: "var(--ink)" }} data-testid={testId}>{children || "—"}</div>
  </div>
);

const SOURCE_LABEL = {
  environment: "Certificate (private key held in secure server settings)",
  secret: "Client secret (held in secure server settings)",
};

export function SsoStatusCard({ credential, lastTest, warnDays }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const days = credential.days_remaining;
  const tone = days === null ? "" : days < 14 ? "pill-danger" : days < warnDays ? "pill-warn" : "pill-success";

  const runTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sso/test");
      setResult(data);
      toast[data.ok ? "success" : "error"](data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="sso-status-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="eyebrow mb-1">Microsoft 365 connection</div>
          <span className={`pill ${credential.enabled ? "pill-success" : "pill-warn"}`} data-testid="sso-status-pill">
            {credential.enabled ? "Configured" : "Not configured"}
          </span>
          {typeof days === "number" && (
            <span className={`pill ml-2 ${tone}`} data-testid="sso-expiry-pill">
              {days < warnDays ? `Certificate expires in ${days} days` : "Certificate valid"}
            </span>
          )}
        </div>
        <button onClick={runTest} disabled={busy || !credential.enabled} className="btn-ghost flex items-center gap-2" data-testid="sso-test-connection">
          <PlugZap size={14} /> {busy ? "Testing…" : "Test connection"}
        </button>
      </div>
      <div className="grid gap-5 md:grid-cols-4">
        <Field label="Tenant ID" testId="sso-tenant-id">{credential.tenant_id}</Field>
        <Field label="Client ID" testId="sso-client-id">{credential.client_id}</Field>
        <Field label="Redirect URI" testId="sso-redirect-uri" wide>{credential.redirect_uri}</Field>
        <Field label="Credential in use" testId="sso-credential-source">{SOURCE_LABEL[credential.source]}</Field>
        <Field label="Valid from" testId="sso-credential-valid-from">{credential.not_before && formatWhen(credential.not_before, false)}</Field>
        <Field label="Expires" testId="sso-credential-expiry">
          {credential.expires_at && (
            <>
              {formatWhen(credential.expires_at, false)}
              <span className="block text-xs" style={{ color: days < 60 ? "#B45309" : "var(--muted)" }}>{days} days remaining</span>
            </>
          )}
        </Field>
        <Field label="Thumbprint (SHA-1, as shown in Entra)" testId="sso-credential-thumbprint" wide>
          <span className="font-mono text-[11px]" style={{ color: "var(--muted)" }}>{credential.thumbprint_sha1}</span>
        </Field>
      </div>
      {!result && lastTest && (
        <p className="mt-5 text-xs" style={{ color: "var(--muted)" }} data-testid="sso-last-test">
          Last tested {formatWhen(lastTest.occurred_at)} · {lastTest.outcome === "success" ? "connected" : "failed"}
        </p>
      )}
      {result && (
        <p className={`mt-5 text-xs ${result.ok ? "" : "font-medium"}`} style={{ color: result.ok ? "var(--accent)" : "#9B2C1F" }} data-testid="sso-test-result">
          {result.ok ? "Connected" : "Failed"} · {formatWhen(result.at)} · {result.message}
        </p>
      )}
    </section>
  );
}
